/**
 * Extension attendue pour les formats stockés dans filemngt. Le dépôt convertit
 * les images en PDF (`getBase64`) tout en conservant le nom d'origine : sans
 * réalignement, un PDF part en « image0.jpeg », illisible une fois téléchargé.
 */
const EXTENSION_BY_FORMAT: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
};

/** Extensions considérées comme équivalentes à celle attendue pour un format. */
const EQUIVALENT_EXTENSIONS: Record<string, string[]> = {
  jpg: ['jpg', 'jpeg'],
};

/**
 * Aligne l'extension du nom sur le format réel du contenu. Un format inconnu
 * laisse le nom intact.
 */
function withFormatExtension(name: string, format?: string): string {
  const expected = format ? EXTENSION_BY_FORMAT[format.toLowerCase()] : undefined;
  if (!expected) {
    return name;
  }
  const dot = name.lastIndexOf('.');
  const current = dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
  if ((EQUIVALENT_EXTENSIONS[expected] || [expected]).includes(current)) {
    return name;
  }
  // Seule une extension connue est remplacée : « Facture 2026.10 » garde son suffixe.
  const base = dot > 0 && Object.values(EXTENSION_BY_FORMAT).concat('jpeg').includes(current)
    ? name.slice(0, dot)
    : name;
  return `${base}.${expected}`;
}

/**
 * Nom proposé au téléchargement d'un fichier lu sur filemngt (`GET /file/{uid}/`).
 *
 * Historiquement, le nom était lu dans `relations.items[0].qualifier`. Or un fichier
 * déposé par le flux stream (`FileStreamController#uploadStream`, utilisé par le
 * panneau de dépôt de la GED) est créé avec des relations vides : la lecture levait
 * une TypeError, avalée par le `.catch` du store, et rien n'était téléchargé
 * (EFLO-1031, prod GEMY). Ce flux renseigne en revanche `originalFileName`.
 *
 * Ordre de résolution : nom affiché par l'appelant (libellé du tiroir), qualifier
 * de la relation, `originalFileName`, puis le nom de repli. L'extension est enfin
 * alignée sur `format` : une photo déposée est stockée en PDF sous son nom d'origine.
 */
export function downloadFileName(response: any, fallback = 'document', preferredName?: string): string {
  const qualifier = response?.relations?.items?.[0]?.qualifier?.rawValue;
  const name = preferredName || qualifier || response?.originalFileName || fallback;
  return withFormatExtension(String(name).replace(/_/g, ' '), response?.format);
}
